import { useCallback, useMemo } from "react";

import { Lane } from "app/pages/bid-award/types";
import { IDeveloperSettings } from "dromo-uploader-js";
import {
  DromoBeforeFinishCallback,
  DromoField,
  DromoOnResultsCallback,
  useDromoUploader,
} from "features/dromo/useDromoUploader";
import pick from "lodash/pick";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useImportContractRequestLanesMutation } from "../api/bidRequestApi";
import { ImportLane } from "../api/types";
import { useUpdateBidRequest } from "../store/bidRequestContractSlice";
import { selectAllFields, useBidRequestSliceSelector } from "../store/bidRequestStore";

const LANE_IMPORT_BATCH_SIZE = 20;
const BATCH_MAX_RETRIES = 3;
const BATCH_RETRY_BASE_DELAY_MS = 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type UseDromoLanesImportHookArgs = {
  onSuccess?(lanes: Lane[]): void;
  onError?(error: unknown): string;
  onFinish?(): void;
  deleteExistingLanes?: boolean;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useDromoLanesImport = ({
  onSuccess,
  onFinish,
  onError,
  deleteExistingLanes,
}: UseDromoLanesImportHookArgs = {}) => {
  const { t } = useTranslation(["shipper", "common"]);
  const { t: fieldNamesT } = useTranslation("shipper", { keyPrefix: "bids.fieldNames" });

  const contractId = useBidRequestSliceSelector((state) => state.bidRequestContractSlice.id);
  const [importContractRequestLanes] = useImportContractRequestLanesMutation();
  const updateBidRequest = useUpdateBidRequest();
  const { hidePoNumberTillDispatch, hideInternalRefTillDispatch } = useBidRequestSliceSelector(
    (state) => state.bidRequestFieldsSlice
  );

  const handleImportDataBeforeFinish = useCallback<DromoBeforeFinishCallback>(
    async (data: ImportLane[], _metadata, _instance) => {
      try {
        if (!contractId) throw new Error("No contract request id provided");
        // change lane_id to shipper_lane_id
        data.forEach((lane) => {
          lane.shipper_lane_id = lane.lane_id;
          delete lane.lane_id;
          lane.hide_po_till_dispatch = !!hidePoNumberTillDispatch;
          lane.hide_internal_ref_till_dispatch = !!hideInternalRefTillDispatch;
          // Default Dromo time output is ISO-8601 (HH:mm)
          Object.entries(lane).forEach(([key, value]) => {
            if ((key.endsWith("appointment_start") || key.endsWith("appointment_end")) && value) {
              lane[key] = pick(DateTime.fromISO(value as string).toObject(), ["hour", "minute"]);
            }
          });
        });

        // Batch lanes into groups of LANE_IMPORT_BATCH_SIZE to prevent request timeouts
        const allLanes: Lane[] = [];
        for (let i = 0; i < data.length; i += LANE_IMPORT_BATCH_SIZE) {
          const batch = data.slice(i, i + LANE_IMPORT_BATCH_SIZE);
          let lastError: unknown;

          for (let attempt = 0; attempt < BATCH_MAX_RETRIES; attempt++) {
            try {
              const lanes = await importContractRequestLanes({
                contractRequestId: contractId,
                lanes: batch,
                deleteExistingLanes: deleteExistingLanes && i === 0, // Only delete existing lanes on the first batch
              }).unwrap();
              allLanes.push(...lanes);
              lastError = undefined;
              break;
            } catch (error) {
              lastError = error;
              if (attempt < BATCH_MAX_RETRIES - 1) {
                await wait(BATCH_RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
              }
            }
          }

          if (lastError) {
            throw lastError;
          }
        }

        // Correct the lanes_count in the store since each batch triggers the
        // store matcher individually, leaving it with only the last batch's count
        updateBidRequest({ lanes_count: allLanes.length });
        onSuccess?.(allLanes);
        return;
      } catch (error) {
        let message = t("shipper:bids.dromoLanesImportError");

        if (onError) {
          message = onError?.(error);
        } else {
          if (error instanceof Object && "data" in error && error.data instanceof Object) {
            if ("error" in error.data && "message" in error.data) {
              message += ` [${error.data.error}: "${error.data.message}"]`;
            }
          }
        }

        return {
          cancel: true,
          message,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contractId, importContractRequestLanes, updateBidRequest, onError, onSuccess, t, hidePoNumberTillDispatch]
  );

  const fields = useBidRequestSliceSelector(selectAllFields);
  const dromoFields: DromoField[] = useMemo(
    (): DromoField[] =>
      fields.map((field) => {
        const { required, selected, label, additionalI18NOptions, hideField, ...dromoFields } = field;

        const dromoField: DromoField = {
          ...dromoFields,
          label: fieldNamesT(label, additionalI18NOptions),
          hideField: hideField || (!required && !selected),
        };

        return dromoField;
      }),
    [fields, fieldNamesT]
  );

  const settings = useMemo<IDeveloperSettings>(() => {
    return {
      title: t("shipper:bids.dromoUploadTitle"),
      reviewStep: {
        helpText: t("shipper:bids.dromoUploadTitle"),
      },
      importIdentifier: "Bid Requests",
      templateDownloadFilename: "bid-request-template",
    };
  }, [t]);

  const handleFinish = useCallback<DromoOnResultsCallback>(
    (_data, _metadata) => {
      onFinish?.();
    },
    [onFinish]
  );

  return useDromoUploader({
    beforeFinish: handleImportDataBeforeFinish,
    onResults: handleFinish,
    settings: settings,
    fields: dromoFields,
  });
};
